import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import LoadingOverlay from "../../components/loadingOverlay";
import PageHeader from "../../components/pageHeader";
import MainContentLayout from "../../components/mainContentLayout";
import { API_BASE } from "../../config/apiConfig";
import { useSelector, useDispatch } from "react-redux";
import { setLogs, clearLogs } from "../../features/ptLogs/ptLogsSlice";
import { FiRefreshCw, FiEye, FiCheck, FiX } from "react-icons/fi";
import { FaPlusCircle } from "react-icons/fa";
import { formatDateTime } from "../../utils/date";

const PAGE_SIZE = 5;

export default function PTLogsPage({ mode = "player" }) {
  const token = sessionStorage.getItem("access_token");
  const user = useSelector((state) => state.user.user);
  const logsByKey = useSelector((state) => state.ptLogs.logsByKey);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { playerId } = useParams();

  const [logs, setLocalLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [page, setPage] = useState(1);

  // Generate cache key
  const targetPlayerId = mode === "player" ? user?.user_id : playerId;
  const key = `${mode}-${targetPlayerId}-${year}-${month}`;

  // Load logs on month/year change
  useEffect(() => {
    setPage(1);
    fetchLogs();
  }, [month, year]);

  // -------------------------
  // FETCH LOGS (with caching)
  // -------------------------
  async function fetchLogs(forceRefresh = false) {
    if (!targetPlayerId) return;

    // If cached AND not refresh → load from cache
    if (logsByKey[key] && !forceRefresh) {
      setLocalLogs(logsByKey[key]);
      return;
    }

    setLoading(true);

    const params = new URLSearchParams({
      mode,
      month,
      year,
      playerId: mode === "coach" ? playerId : "",
    });

    try {
      const res = await fetch(`${API_BASE}/api/pt-logs-list?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      if (!data.success) {
        console.error("Failed to fetch logs:", data.message);
        return;
      }

      // Save to Redux
      dispatch(setLogs({ key, logs: data.logs }));

      // Save locally for pagination
      setLocalLogs(data.logs);
    } catch (err) {
      console.error("Error fetching logs:", err);
    } finally {
      setLoading(false);
    }
  }

  // -------------------------
  // Manual Refresh (coach only)
  // -------------------------
  function handleRefresh() {
    dispatch(clearLogs({ key })); // clear cache for this month/year/player
    fetchLogs(true);              // force refresh
  }

  // -------------------------
  // Pagination
  // -------------------------
  const startIndex = (page - 1) * PAGE_SIZE;
  const paginated = logs.slice(startIndex, startIndex + PAGE_SIZE);
  const totalPages = Math.ceil(logs.length / PAGE_SIZE);

  return (
    <MainContentLayout>
      <PageHeader title="1 to 1 Training Logs" backTo={mode === "player" ? "/dashboard" : `/pt-logs-player-list`} />
      <div className="w-full px-4">

        {/* Filters & Refresh */}
        <div className="flex gap-4 items-center">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="border border-gray-400 rounded px-2 py-2"
          >
            {Array.from({ length: 12 }).map((_, i) => (
              <option key={i} value={i + 1}>
                {new Date(0, i).toLocaleString("default", { month: "long" })}
              </option>
            ))}
          </select>

          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="border border-gray-400 rounded px-2 py-2"
          >
            {Array.from({ length: 5 }).map((_, i) => {
              const y = new Date().getFullYear() - i;
              return (
                <option key={y} value={y}>
                  {y}
                </option>
              );
            })}
          </select>

          {/* Coach Refresh Button */}
          {mode === "coach" && (
            <button
              onClick={handleRefresh}
              className="ml-auto p-2 text-blue-600 hover:text-blue-800 transition"
              title="Refresh"
            >
              <FiRefreshCw size={20} />
            </button>
          )}

          {/* Player Add Button */}
          {mode === "player" && (
            <button
              onClick={() => navigate("/add-pt-log")}
              className="ml-auto p-1 text-blue-600 hover:text-blue-800"
              title="Add New Record"
            >
              <FaPlusCircle size={26} />
            </button>
          )}
        </div>

        {/* Logs List */}
        {logs.length === 0 ? (
          <p className="mt-4">No logs for this month.</p>
        ) : (
          <table className="w-full border-collapse border border-gray-300 mt-4">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-2">#</th>
                <th className="border p-2">Date & Time</th>
                <th className="border p-2">Coach Commented</th>
                <th className="border p-2"></th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((log, idx) => (
                <tr key={log.id}>
                  <td className="border p-2 text-center">
                    {startIndex + idx + 1}
                  </td>
                  <td className="border p-2">
                    {formatDateTime(log.date)}
                  </td>
                  <td className="border p-2">
                    <div className="flex justify-center items-center h-full">
                      {log.isCommented ? (
                        <FiCheck size={20} className="text-green-600" />
                      ) : (
                        <FiX size={20} className="text-red-500" />
                      )}
                    </div>
                  </td>
                  <td className="border p-2 text-center">
                    <button
                      onClick={() =>
                        navigate(
                          mode === "player"
                            ? `/view-pt-log/${log.id}`
                            : `/view-pt-log/${playerId}/${log.id}`
                        )
                      }
                      className="text-green-600 hover:text-green-800"
                      title="View Log"
                    >
                      <FiEye size={20} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex justify-between items-center">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className={`px-3 py-1 rounded ${
                page === 1 ? "bg-gray-200 text-gray-400" : "bg-blue-600 text-white"
              }`}
            >
              Prev
            </button>
            <span>
              Page {page} / {totalPages}
            </span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              className={`px-3 py-1 rounded ${
                page === totalPages
                  ? "bg-gray-200 text-gray-400"
                  : "bg-blue-600 text-white"
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>
      <LoadingOverlay show={loading} text="Loading..." />
    </MainContentLayout>
  );
}
